/**
 * KB view — shared color-mapping helpers.
 */

/**
 * Accent token for a KB type.
 *
 * Covers all twelve types in KB_TYPES. It previously covered eight, so `adr`,
 * `invariant`, `idiom` and `rule` — 101 entries in this project, including
 * every ADR — all fell through to the `t3` grey and were indistinguishable from
 * each other. That is survivable in a list where the type is written next to
 * the dot; it is not survivable in a graph where colour IS the encoding.
 *
 * Twelve categories is past the number of hues anyone can reliably tell apart,
 * so the three smallest types deliberately SHARE a hue with their nearest
 * sibling rather than inventing near-identical colours:
 *
 *   adr   → shares `decision`'s purple  (both record a decision)
 *   idiom → shares `pattern`'s cyan     (both are a recurring shape)
 *   rule  → shares `convention`'s pink  (both are a standing instruction)
 *
 * Those three total 52 entries. The legend and hover carry the exact type.
 *
 * `insight` moves off cyan onto its own blue. It collided with `pattern`
 * before, which mattered little in a list and a great deal in a graph: the two
 * together are 513 entries, so a third of the nodes would have rendered as one
 * colour and the clustering would have been unreadable.
 */
export function kbTypeColor(type: string): string {
  const map: Record<string, string> = {
    // Largest types get their own hue — these carry the visual weight.
    learning: 'grn',
    insight: 'blu',
    pattern: 'cyn',
    convention: 'pnk',
    antipattern: 'err',
    // Mid-sized types.
    invariant: 'tea',
    decision: 'pur',
    debt: 'wrn',
    procedure: 'org',
    // Smallest types, sharing a hue with their sibling above.
    adr: 'pur',
    idiom: 'cyn',
    rule: 'pnk',
  }
  return map[type] ?? 't3'
}

export function workStatusColor(status: string): string {
  const map: Record<string, string> = {
    done: 'grn',
    in_progress: 'cyn',
    cancelled: 'err',
    open: 't4',
  }
  return map[status] ?? 't3'
}
